import AddIcon from '@mui/icons-material/Add'
import ArchiveOutlined from '@mui/icons-material/ArchiveOutlined'
import DeleteOutlined from '@mui/icons-material/DeleteOutlined'
import DraftsOutlined from '@mui/icons-material/DraftsOutlined'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import FolderOutlined from '@mui/icons-material/FolderOutlined'
import InboxOutlined from '@mui/icons-material/InboxOutlined'
import MoreVert from '@mui/icons-material/MoreVert'
import ReportGmailerrorredOutlined from '@mui/icons-material/ReportGmailerrorredOutlined'
import ScheduleSendOutlined from '@mui/icons-material/ScheduleSendOutlined'
import SendOutlined from '@mui/icons-material/SendOutlined'
import StarBorder from '@mui/icons-material/StarBorder'
import { useDroppable } from '@dnd-kit/core'
import { Badge, Box, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useState, type ReactElement } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import { keys, useFolders, useLabels, useScheduled } from '../api/hooks'
import type { FolderRole, Label } from '../api/types'
import { labelTree } from '../lib/cache'
import { confirmDialog } from '../lib/dialogs'
import { useUi } from '../store/ui'
import { useTokens } from '../theme'

const ICONS: Record<FolderRole, ReactElement> = {
  inbox: <InboxOutlined />, sent: <SendOutlined />, drafts: <DraftsOutlined />, trash: <DeleteOutlined />,
  spam: <ReportGmailerrorredOutlined />, archive: <ArchiveOutlined />, other: <FolderOutlined />,
}
const MAIN: FolderRole[] = ['inbox', 'sent', 'drafts', 'archive', 'spam', 'trash']
const TITLES: Record<FolderRole, string> = { inbox: 'Inbox', sent: 'Sent', drafts: 'Drafts', archive: 'Archive', spam: 'Spam', trash: 'Trash', other: '' }

interface ItemProps { dropId?: string; selected: boolean; onClick: () => void; icon: ReactElement; label: string; count?: number; depth?: number; collapsed: boolean; extra?: ReactElement }

function NavItem({ dropId, selected, onClick, icon, label, count, depth = 0, collapsed, extra }: ItemProps) {
  const t = useTokens()
  const { setNodeRef, isOver } = useDroppable({ id: dropId ?? `none:${label}`, disabled: !dropId })
  const active = selected || isOver
  const body = (
    <Box ref={setNodeRef} role="link" tabIndex={0} aria-current={selected ? 'page' : undefined} aria-label={collapsed ? label : undefined}
      onClick={onClick} onKeyDown={(e) => { if (e.key === 'Enter') onClick() }}
      sx={{ display: 'flex', alignItems: 'center', height: 36, cursor: 'pointer', borderRadius: '10px', mx: collapsed ? 'auto' : 1, pl: collapsed ? 0 : `${12 + depth * 14}px`, pr: collapsed ? 0 : 1, justifyContent: collapsed ? 'center' : 'flex-start', width: collapsed ? 44 : 'auto',
        bgcolor: active ? t.selected : 'transparent', color: selected ? t.accent : 'text.primary', fontWeight: selected ? 700 : count ? 600 : 500, outline: isOver ? '2px dashed' : 'none', outlineColor: 'primary.main', transition: 'background-color 150ms', '&:hover': { bgcolor: active ? t.selected : t.hover }, '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' } }}>
      <Badge color="default" badgeContent={collapsed ? count : 0} max={99} sx={{ '& .MuiBadge-badge': { bgcolor: t.accent, color: t.surface } }}>{icon}</Badge>
      {!collapsed && (
        <>
          <Typography noWrap sx={{ flex: 1, ml: 1.5, fontWeight: 'inherit', fontSize: 14 }}>{label}</Typography>
          {count ? <Typography component="span" className="tabular" sx={{ fontSize: 12, fontWeight: 700, minWidth: 22, textAlign: 'center', px: 0.75, borderRadius: '8px', bgcolor: selected ? 'background.paper' : t.hover, color: selected ? t.accent : 'text.secondary' }}>{count}</Typography> : null}
          {extra}
        </>
      )}
    </Box>
  )
  return collapsed ? <Tooltip title={label} placement="right">{body}</Tooltip> : body
}

export function Sidebar({ onNavigate, collapsed = false }: { onNavigate?: () => void; collapsed?: boolean }) {
  const folders = useFolders()
  const labels = useLabels()
  const scheduled = useScheduled()
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const qc = useQueryClient()
  const t = useTokens()
  const { notify, setLabelDialog } = useUi()
  const [labelsOpen, setLabelsOpen] = useState(true)
  const [collapsedLabels, setCollapsedLabels] = useState<Set<string>>(new Set())
  const [menu, setMenu] = useState<{ el: HTMLElement; label: Label } | null>(null)
  const go = (to: string) => { navigate(to); onNavigate?.() }

  const all = folders.data ?? []
  const byRole = (r: FolderRole) => all.find((f) => f.role === r)
  const others = all.filter((f) => f.role === 'other')
  const tree = labelTree(labels.data ?? [])
  const visible = tree.filter((l) => { let p = l.parentId; while (p) { if (collapsedLabels.has(p)) return false; p = tree.find((x) => x.id === p)?.parentId ?? null } return true })
  const starred = pathname === '/mail/s' && new URLSearchParams(search).get('q') === 'is:starred'

  const mutateLabel = async (fn: () => Promise<unknown>) => {
    try { await fn(); await qc.invalidateQueries({ queryKey: keys.labels }) } catch (e) { notify({ message: e instanceof Error ? e.message : 'Failed', severity: 'error' }) }
  }
  const toggleKids = (id: string) => setCollapsedLabels((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n })

  return (
    <Box component="nav" aria-label="Mailboxes and labels" sx={{ height: '100%', overflowY: 'auto', overflowX: 'hidden', pb: 2, bgcolor: t.rail, '& > div > [role=link]': { mb: '2px' } }}>
      <Box>
        {MAIN.slice(0, 1).map((r) => { const f = byRole(r); return f && <NavItem key={r} collapsed={collapsed} dropId={`folder:${f.path}`} selected={pathname === `/mail/f/${r}` || pathname.startsWith(`/mail/f/${r}/`)} onClick={() => go(`/mail/f/${r}`)} icon={ICONS[r]} label={TITLES[r]} count={f.unseen} /> })}
        <NavItem collapsed={collapsed} selected={starred} onClick={() => go(`/mail/s?q=${encodeURIComponent('is:starred')}`)} icon={<StarBorder />} label="Starred" />
        {MAIN.slice(1, 2).map((r) => { const f = byRole(r) ?? (all.length ? { path: TITLES[r], unseen: 0 } : undefined); return f && <NavItem key={r} collapsed={collapsed} dropId={`folder:${f.path}`} selected={pathname === `/mail/f/${r}` || pathname.startsWith(`/mail/f/${r}/`)} onClick={() => go(`/mail/f/${r}`)} icon={ICONS[r]} label={TITLES[r]} /> })}
        <NavItem collapsed={collapsed} selected={pathname === '/mail/scheduled'} onClick={() => go('/mail/scheduled')} icon={<ScheduleSendOutlined />} label="Scheduled" count={scheduled.jobs.length || undefined} />
        {MAIN.slice(2).map((r) => { const f = byRole(r) ?? (all.length ? { path: TITLES[r], unseen: 0 } : undefined); return f && <NavItem key={r} collapsed={collapsed} dropId={`folder:${f.path}`} selected={pathname === `/mail/f/${r}` || pathname.startsWith(`/mail/f/${r}/`)} onClick={() => go(`/mail/f/${r}`)} icon={ICONS[r]} label={TITLES[r]} count={r === 'drafts' || r === 'trash' || r === 'archive' ? undefined : f.unseen} /> })}
        {others.map((f) => <NavItem key={f.path} collapsed={collapsed} dropId={`folder:${f.path}`} selected={pathname.startsWith(`/mail/d/${encodeURIComponent(f.path)}`)} onClick={() => go(`/mail/d/${encodeURIComponent(f.path)}`)} icon={ICONS.other} label={f.name} count={f.unseen} />)}
      </Box>

      {!collapsed && (
        <Box sx={{ mt: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', px: 2.5, height: 32 }}>
            <Typography variant="caption" sx={{ flex: 1, fontWeight: 700, color: 'text.secondary', letterSpacing: '0.04em' }}>LABELS</Typography>
            <Tooltip title="Create new label"><IconButton size="small" aria-label="Create label" onClick={() => setLabelDialog({})}><AddIcon fontSize="small" /></IconButton></Tooltip>
            <IconButton size="small" aria-label={labelsOpen ? 'Collapse labels' : 'Expand labels'} onClick={() => setLabelsOpen((v) => !v)}>{labelsOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}</IconButton>
          </Box>
          {labelsOpen && (
            <>
              {visible.map((l) => {
                const hasKids = tree.some((x) => x.parentId === l.id)
                return (
                  <NavItem key={l.id} collapsed={false} dropId={`label:${l.id}`} depth={l.depth} selected={pathname.startsWith(`/mail/l/${l.id}`)} onClick={() => go(`/mail/l/${l.id}`)} label={l.name} count={l.unread}
                    icon={<Box aria-hidden sx={{ width: 12, height: 12, m: '4px', borderRadius: '4px', bgcolor: l.color }} />}
                    extra={
                      <Box sx={{ display: 'flex', ml: 0.5 }} onClick={(e) => e.stopPropagation()}>
                        {hasKids && <IconButton size="small" aria-label={collapsedLabels.has(l.id) ? `Expand ${l.name}` : `Collapse ${l.name}`} onClick={() => toggleKids(l.id)} sx={{ p: 0 }}>{collapsedLabels.has(l.id) ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}</IconButton>}
                        <IconButton size="small" aria-label={`Label options for ${l.name}`} onClick={(e) => setMenu({ el: e.currentTarget, label: l })} sx={{ p: 0 }}><MoreVert fontSize="small" /></IconButton>
                      </Box>
                    } />
                )
              })}
              {!labels.data?.length && <Typography variant="caption" color="text.secondary" sx={{ px: 2.5 }}>No labels yet. Use + to create one.</Typography>}
            </>
          )}
        </Box>
      )}

      <Menu open={Boolean(menu)} anchorEl={menu?.el} onClose={() => setMenu(null)}>
        <MenuItem onClick={() => { const l = menu!.label; setMenu(null); setLabelDialog({ label: l }) }}>Edit</MenuItem>
        <MenuItem onClick={() => { const l = menu!.label; setMenu(null); setLabelDialog({ parentId: l.id }) }}>Add sub-label</MenuItem>
        <MenuItem sx={{ color: 'error.main' }} onClick={() => { const l = menu!.label; setMenu(null); void confirmDialog({ title: `Delete label “${l.name}”?`, body: 'The label is removed from all messages. The messages themselves are kept.', confirmLabel: 'Delete', danger: true }).then((ok) => { if (ok) void mutateLabel(() => api(`/labels/${l.id}`, { method: 'DELETE' })) }) }}>Delete</MenuItem>
      </Menu>
    </Box>
  )
}
